import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";

import { customersService, locationService } from "../../services";

const PHONE_MAX_LENGTH = 20;
const normalizePhoneValue = (value) => String(value || "").replace(/[^\d]/g, "").slice(0, PHONE_MAX_LENGTH);
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const extractErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") {
    return "No se pudo crear el cliente.";
  }
  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return "No se pudo crear el cliente.";
};

function CustomerQuickCreateDialog({
  open,
  phoneNumber = "",
  onClose,
  onCreated,
}) {
  const [form, setForm] = useState({
    name: "",
    phone_number: "",
    nit: "",
    email: "",
    department_id: "",
    city_id: "",
    address: "",
  });
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadDepartments = useCallback(async () => {
    try {
      const departments = await locationService.listDepartments({ active: "true" });
      setDepartmentOptions(toArray(departments));
    } catch {
      setDepartmentOptions([]);
    }
  }, []);

  const loadCities = useCallback(async (departmentId = "") => {
    setLoadingCities(true);
    try {
      const cities = await locationService.listCities({ active: "true", department_id: departmentId || undefined });
      setCityOptions(toArray(cities));
    } catch {
      setCityOptions([]);
    } finally {
      setLoadingCities(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: "",
      phone_number: normalizePhoneValue(phoneNumber),
      nit: "",
      email: "",
    city_id: "",
      department_id: "",
    address: "",
    });
    setSubmitting(false);
    setError("");
    loadDepartments();
    setCityOptions([]);
  }, [loadDepartments, open, phoneNumber]);

  const canSubmit = useMemo(() => {
    return Boolean(String(form.name || "").trim()) && Boolean(String(form.phone_number || "").trim()) && !submitting;
  }, [form.name, form.phone_number, submitting]);

  const handleChange = (field) => (event) => {
    const rawValue = event?.target?.value ?? "";
    const value = field === "phone_number" ? normalizePhoneValue(rawValue) : rawValue;
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "department_id" ? { city_id: "" } : {}),
    }));
    if (field === "department_id") loadCities(value);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const customer = await customersService.create({
        name: String(form.name || "").trim(),
        phone_number: normalizePhoneValue(form.phone_number),
        nit: String(form.nit || "").trim() || undefined,
        email: String(form.email || "").trim() || undefined,
        city_id: form.city_id || undefined,
        address: String(form.address || "").trim() || undefined,
        is_active: true,
      });
      onCreated?.(customer);
    } catch (nextError) {
      setError(extractErrorMessage(nextError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Crear cliente</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.2} sx={{ pt: 0.4 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Telefono"
            size="small"
            value={form.phone_number}
            onChange={handleChange("phone_number")}
            helperText="Este telefono se usara para identificar al cliente."
            inputProps={{ maxLength: PHONE_MAX_LENGTH, inputMode: "numeric" }}
            fullWidth
          />
          <TextField
            label="Nombre completo"
            size="small"
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            autoFocus
          />
          <TextField
            label="NIT"
            size="small"
            value={form.nit}
            onChange={handleChange("nit")}
            fullWidth
          />
          <TextField
            label="Correo"
            size="small"
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            helperText="Opcional. Si no tiene correo, dejalo en blanco."
            fullWidth
          />
          <TextField
            select
            label="Departamento"
            size="small"
            value={form.department_id}
            onChange={handleChange("department_id")}
            fullWidth
          >
            <MenuItem value="">Sin departamento</MenuItem>
            {departmentOptions.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                {department.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Municipio/Ciudad"
            size="small"
            value={form.city_id}
            onChange={handleChange("city_id")}
            disabled={loadingCities || !form.department_id}
            fullWidth
          >
            <MenuItem value="">Sin municipio</MenuItem>
            {cityOptions.map((city) => (
              <MenuItem key={city.id} value={city.id}>
                {city.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Direccion"
            size="small"
            value={form.address}
            onChange={handleChange("address")}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {submitting ? "Guardando..." : "Crear cliente"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CustomerQuickCreateDialog;
